import React, { useState, useEffect } from "react";
import { Button, FormControl, Grid, Typography, Divider, InputLabel, Select, MenuItem, Alert } from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import IconButton from "@mui/material/IconButton";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { makeStyles } from "@mui/styles";
import ConfirmationDialog from "../components/fundamentals/ConfirmationDialog";
import {
  ENTER_PREFERENCE,
  PREFERENCES_ENTERED,
  CLEAR_ALL_PREFERENCES_MESSAGE,
  CLEAR_ALL_PREFERENCES,
  DUPLICATE_PREFERENCE_ALERT_MESSAGE,
  NO_PREFERENCE_ALERT_MESSAGE,
  NO_ROWS_MESSAGE,
} from "../util/messageConstants";
import { isDuplicatePreference } from "../util/commonUtils";
import {
  PERMANENT_BID_DAYSOFF,
  IAM_PERMANENT_BID_PREFERENCES,
  IAM_PERMANENT_BID_PREFERENCES_FOR_HOLIDAY,
  PERMANENT_BID_DAYSOFF_ON_WEEKENDS,
  PERMANENT_BID_START_TIME_VALUES,
  TWU_PERMANENT_BID_PREFERENCES,
  DAYS_OFF,
  SHIFT,
  PREFERENCE,
  TWU_PERMANENT_BID_PREFERENCES_FOR_HOLIDAY,
  RELIEF,
} from "../util/constants";
import { areDuplicatePreferencesExists, validatePreferences } from "../util/permanentBidInputValidation";
import { darken, lighten } from "@mui/material/styles";
import { BID_AWARD_TYPE } from "../util/bid/bidConstants";

const getBackgroundColor = (color, mode) => (mode === "dark" ? darken(color, 0.7) : lighten(color, 0.7));
const getHoverBackgroundColor = (color, mode) => (mode === "dark" ? darken(color, 0.6) : lighten(color, 0.6));

const useStyles = makeStyles((theme) => ({
  buttonStyle: {
    padding: "15px",
  },
  inputSelect: {
    textAlign: "left",
    marginRight: "10px",
    background: "white",
  },
  preferenceTitle: {
    color: theme.palette.primary.swaBlue,
    fontSize: "12px",
  },
  selectMargin: {
    marginTop: "20px",
  },
  tableStyle: {
    height: "25vh",
  },
  gridStyle: {
    width: "100%",
    textAlign: "left",
  },
  clearButton: {
    textDecoration: "underline",
    fontSize: "16px",
  },
  blueColumn: {
    backgroundColor: "red !important",
  },
  enterPreference: {
    padding: 16,
    paddingTop: 24,
    backgroundColor: theme.palette.secondary.swaGray1,
  },
  preferenceFont: {
    fontSize: "16px",
  },
  bidMobilePref: {
    padding: "16px",
    backgroundColor: theme.palette.secondary.swaGray1,
  },
  bidPaddingTop: {
    paddingTop: "16px",
  },
  mobileTableScroll: {
    width: "100%",
    height: "48vh",
    overflowY: "scroll",
    position: "absolute",
    overflowX: "scroll",
  },
  enteredPref: {
    paddingLeft: "16px",
    background: theme.palette.secondary.swaGray2,
  },
  table: (props) => ({
    borderRadius: 0,
    border: "none",
    "& div": {
      "& .MuiDataGrid-footerContainer": {
        display: "none",
      },
      "& .MuiDataGrid-columnSeparator": {
        display: "none",
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        color: theme.palette.primary.swaBlue,
        fontWeight: "bold",
      },
      "& .super-app-theme--true": {
        backgroundColor: getBackgroundColor(theme.palette.error.main, theme.palette.mode),
        "&:hover": {
          backgroundColor: getHoverBackgroundColor(theme.palette.error.main, theme.palette.mode),
        },
      },
    },
  }),
}));

const PermanentBidPreference = (props) => {
  const classes = useStyles();
  const [startTime, setStartTime] = useState("");
  const [daysOff, setDaysOff] = useState("");
  const [position, setPosition] = useState("");
  const [isClearAllDialogOpen, setIsClearAllDialogOpen] = useState(false);
  const [duplicateRecordsAlert, setDuplicateRecordsAlert] = useState(false);
  const { permanentBid, setPermanentBid, twuContract, noPreferencesAlert, setNoPreferencesAlert } = props;
  const [addDisable, setAddDisable] = useState(false);
  const [permanentBidPreferences, setPermanentBidPreferences] = useState(
    twuContract ? TWU_PERMANENT_BID_PREFERENCES : IAM_PERMANENT_BID_PREFERENCES
  );

  const getColumns = () => {
    let columns = [{ field: "id", headerName: "", sortable: false, width: 52, minWidth: 25 }];

    if (!props.isReadOnly) {
      columns.push(
        {
          field: "arrowup",
          headerName: "",
          headerAlign: "left",
          align: "left",
          type: "string",
          width: 44,
          minWidth: 25,
          sortable: false,
          className: classes.blueColumn,
          renderCell: (params) => {
            return renderActionsArrowUp(params.row);
          },
        },
        {
          field: "arrowdown",
          headerName: "",
          headerAlign: "left",
          align: "left",
          type: "string",
          width: 44,
          minWidth: 25,
          sortable: false,
          className: classes.blueColumn,
          renderCell: (params) => {
            return renderActionsArrowDown(params.row);
          },
        }
      );
    }

    columns.push(
      {
        field: "daysOff",
        headerName: "Days Off",
        headerAlign: "left",
        align: "left",
        width: 79,
        sortable: false,
      },
      {
        field: "position",
        headerName: twuContract ? "Shift" : "Preference",
        headerAlign: "left",
        align: "left",
        width: 98,
        sortable: false,
      },
      {
        field: "startTime",
        headerName: "Start Time",
        headerAlign: "left",
        align: "left",
        type: "number",
        width: 97,
        sortable: false,
      }
    );

    if (!props.isReadOnly) {
      columns.push({
        field: "action",
        headerName: "",
        headerAlign: "left",
        align: "left",
        type: "string",
        width: 44,
        minWidth: 25,
        sortable: false,
        renderCell: (params) => {
          return renderActionsCell(params.row);
        },
      });
    }
    return columns;
  };

  const apiRef = useGridApiRef();

  useEffect(() => {
    if (!daysOff || !startTime || !position || duplicateRecordsAlert) {
      setAddDisable(true);
    } else {
      setAddDisable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permanentBid, daysOff, position, startTime]);

  const updateRowPosition = (initialIndex, newIndex, rows) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const rowsClone = [...rows];
        const row = rowsClone.splice(initialIndex, 1)[0];
        rowsClone.splice(newIndex, 0, row);
        resolve(rowsClone);
      }, Math.random() * 500 + 100);
    });
  };

  const handleNoClickedClearAllDialog = () => {
    setIsClearAllDialogOpen(false);
  };

  const handleClickClearAllDialogOpen = () => {
    setIsClearAllDialogOpen(true);
  };

  const handleClearAllDialogYesButtonClicked = () => {
    setIsClearAllDialogOpen(false);
    setPermanentBid([]);
    setPosition("");
    setDaysOff("");
    setStartTime("");
    setDuplicateRecordsAlert(false);
  };

  const renderClearAllDialog = () => {
    return (
      <ConfirmationDialog
        title={CLEAR_ALL_PREFERENCES}
        isDialogOpen={isClearAllDialogOpen}
        message={CLEAR_ALL_PREFERENCES_MESSAGE}
        handleNoButtonClicked={handleNoClickedClearAllDialog}
        handleYesButtonClicked={handleClearAllDialogYesButtonClicked}
      />
    );
  };

  const renderActionsCell = (value) => {
    return (
      <Grid width={10}>
        <IconButton
          sx={{ padding: 0 }}
          onClick={() => {
            var filteredData = permanentBid.filter((permanentBid) => permanentBid.id !== value.id);
            filteredData.map((item, index) => (item.id = index + 1));
            setPermanentBid(filteredData);
            const validatedPreferences = validatePreferences(filteredData);
            setPermanentBid(validatedPreferences);
            setDuplicateRecordsAlert(areDuplicatePreferencesExists(filteredData));
          }}
        >
          <RemoveCircleOutlineIcon color="primary" />
        </IconButton>
      </Grid>
    );
  };

  const renderActionsArrowUp = (value) => {
    return (
      <Grid width={10}>
        <IconButton
          sx={{ padding: 0 }}
          disabled={value.id === 1 ? true : false}
          onClick={async () => {
            if (value.id > 1) {
              const newRows = await updateRowPosition(value.id - 1, value.id - 2, permanentBid);
              newRows.map((item, index) => (item.id = index + 1));
              setPermanentBid(newRows);
            }
          }}
        >
          <ArrowCircleUpIcon color="primary" />
        </IconButton>
      </Grid>
    );
  };
  const renderActionsArrowDown = (value) => {
    return (
      <Grid width={10}>
        <IconButton
          sx={{ padding: 0 }}
          disabled={value.id === permanentBid?.length ? true : false}
          onClick={async () => {
            if (value.id !== permanentBid?.length) {
              const newRows = await updateRowPosition(value.id - 1, value.id, permanentBid);
              newRows.map((item, index) => (item.id = index + 1));
              setPermanentBid(newRows);
            }
          }}
        >
          <ArrowCircleDownIcon color="primary" />
        </IconButton>
      </Grid>
    );
  };
  const renderItem = (item) => {
    return (
      <MenuItem key={item} value={item}>
        {item}
      </MenuItem>
    );
  };
  const handleAdd = () => {
    let currentPreference = {
      daysOff,
      position,
      startTime,
    };
    const result = isDuplicatePreference(permanentBid, currentPreference);
    let [startTimesplit, endTimeSplit] = currentPreference.startTime.split("-");
    setDuplicateRecordsAlert(result);
    let data = {
      id: permanentBid?.length + 1 || 1,
      daysOff,
      position,
      startTime,
      startTimeStartRange: startTimesplit.replace(":", ""),
      startTimeEndRange: endTimeSplit.replace(":", ""),
      message: !!result ? DUPLICATE_PREFERENCE_ALERT_MESSAGE : null,
    };
    setPermanentBid([...permanentBid, data]);
    setNoPreferencesAlert(false);

    const id = permanentBid.length;
    apiRef.current.updateRows([{ id, isNew: true }]);
    setTimeout(() => {
      apiRef.current.scrollToIndexes({
        rowIndex: apiRef.current.getRowsCount() - 1,
      });
    });
  };

  const handleDaysOffChange = (event) => {
    setDaysOff(event.target.value);
    if (PERMANENT_BID_DAYSOFF_ON_WEEKENDS.includes(event.target.value)) {
      twuContract
        ? setPermanentBidPreferences(TWU_PERMANENT_BID_PREFERENCES_FOR_HOLIDAY)
        : setPermanentBidPreferences(IAM_PERMANENT_BID_PREFERENCES_FOR_HOLIDAY);
      if (position === RELIEF) {
        setPosition("");
      }
    } else {
      twuContract
        ? setPermanentBidPreferences(TWU_PERMANENT_BID_PREFERENCES)
        : setPermanentBidPreferences(IAM_PERMANENT_BID_PREFERENCES);
    }
  };

  return (
    <>
      <Divider />
      <Grid className={props.isDesktopView ? classes.enterPreference : classes.bidMobilePref}>
        <Typography className={classes.preferenceTitle}>
          <b>{ENTER_PREFERENCE}</b>
        </Typography>
        <Grid container item alignItems="center" className={classes.selectMargin} md={8} lg={6}>
          <Grid item xs={6} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>{DAYS_OFF}</InputLabel>
              <Select
                id="daysoffSelectElement"
                className={classes.inputSelect}
                value={daysOff}
                onChange={(event) => handleDaysOffChange(event)}
                label={DAYS_OFF}
                disabled={props.isReadOnly}
              >
                <MenuItem value="" disabled>
                  <em>{DAYS_OFF}</em>
                </MenuItem>
                {PERMANENT_BID_DAYSOFF.slice().map((daysOff) => {
                  return renderItem(daysOff);
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            {twuContract ? (
              <FormControl fullWidth>
                <InputLabel>{SHIFT}</InputLabel>
                <Select
                  id="shiftSelectElement"
                  className={classes.inputSelect}
                  value={position}
                  onChange={(event) => setPosition(event.target.value)}
                  label={SHIFT}
                  disabled={props.isReadOnly}
                >
                  <MenuItem value="" disabled>
                    <em>{SHIFT}</em>
                  </MenuItem>
                  {permanentBidPreferences.slice().map((shiftValue) => {
                    return renderItem(shiftValue);
                  })}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth>
                <InputLabel>{PREFERENCE}</InputLabel>
                <Select
                  id="preferenceSelectElement"
                  className={classes.inputSelect}
                  value={position}
                  onChange={(event) => setPosition(event.target.value)}
                  label={PREFERENCE}
                  disabled={props.isReadOnly}
                >
                  <MenuItem value="" disabled>
                    <em>{PREFERENCE}</em>
                  </MenuItem>
                  {permanentBidPreferences.slice().map((preferenceValue) => {
                    return renderItem(preferenceValue);
                  })}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={6} sm={6} md={3} className={props.isDesktopView ? undefined : classes.bidPaddingTop}>
            <FormControl fullWidth>
              <InputLabel>Start Time</InputLabel>
              <Select
                id="startTimeSelectElement"
                className={classes.inputSelect}
                value={startTime}
                onChange={(event) => setStartTime(event.target.value)}
                label="Start Time"
                disabled={props.isReadOnly}
              >
                <MenuItem value="" disabled>
                  <em>Start Time</em>
                </MenuItem>
                {PERMANENT_BID_START_TIME_VALUES.slice().map((startTimeValue) => {
                  return renderItem(startTimeValue);
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={6} md={3} className={props.isDesktopView ? undefined : classes.bidPaddingTop}>
            <Button
              variant="contained"
              className={classes.buttonStyle}
              onClick={() => handleAdd()}
              disabled={addDisable || props.isReadOnly}
            >
              ADD
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <Grid container item justifyContent="space-between" alignItems="center" className={classes.enteredPref}>
        <Grid item className={classes.preferenceFont}>
          <div>
            <b>{PREFERENCES_ENTERED}</b>
            {permanentBid?.length}
          </div>
        </Grid>
        <Grid item>
          <Button
            className={classes.clearButton}
            onClick={() => handleClickClearAllDialogOpen()}
            disabled={!permanentBid?.length || props.isReadOnly}
          >
            CLEAR ALL
          </Button>
        </Grid>
      </Grid>
      <Divider />
      <div>{duplicateRecordsAlert && <Alert severity="error">{DUPLICATE_PREFERENCE_ALERT_MESSAGE} </Alert>}</div>
      {noPreferencesAlert && <Alert severity="error">{NO_PREFERENCE_ALERT_MESSAGE} </Alert>}
      <div className={props.isDesktopView ? classes.tableStyle : classes.mobileTableScroll}>
        <DataGridPro
          className={classes.table}
          rows={permanentBid}
          apiRef={apiRef}
          columns={getColumns()}
          density={props.isDesktopView ? "compact" : "standard"}
          disableColumnMenu={true}
          localeText={NO_ROWS_MESSAGE}
          initialState={
            props.isDesktopView
              ? { pinnedColumns: { left: ["id"] } }
              : { pinnedColumns: { left: ["id", "arrowup", "arrowdown"], right: ["remove"] } }
          }
          sx={{
            "& .selectedAwardedBid": {
              backgroundColor: "primary.swaYellow",
            },
          }}
          getRowClassName={(params) => {
            if (
              props.selectedAwardedBid?.awardType === BID_AWARD_TYPE.PERMANENT_BID &&
              params.row.id === props.selectedAwardedBid.permanentBidPreferenceUsed
            ) {
              return "selectedAwardedBid";
            }
            return `super-app-theme--${params.row.message && true}`;
          }}
        />
      </div>
      {renderClearAllDialog()}
    </>
  );
};

export default PermanentBidPreference;
